import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'

// Auth Pages
import Login from './pages/auth/Login'
import SignUp from './pages/auth/SignUp'
import ForgotPassword from './pages/auth/ForgotPassword'
import ResetPassword from './pages/auth/ResetPassword'

// Client Portal
import ClientLayout from './layouts/ClientLayout'
import GuestLayout from './layouts/GuestLayout'
import Dashboard from './pages/client/Dashboard'
import Garage from './pages/client/Garage'
import VehicleDetail from './pages/client/VehicleDetail'
import MaintenanceAlerts from './pages/client/MaintenanceAlerts'
import Valuations from './pages/client/Valuations'
import Discover from './pages/client/Discover'
import Community from './pages/client/Community'
import Clubs from './pages/client/Clubs'
import Events from './pages/client/Events'
import Specialists from './pages/client/Specialists'
import ServiceHistory from './pages/client/ServiceHistory'
import AddServiceRecord from './pages/client/AddServiceRecord'
import Appointments from './pages/client/Appointments'
import Concierge from './pages/client/Concierge'
import Documents from './pages/client/Documents'
import Settings from './pages/client/Settings'
import AddVehicle from './pages/client/AddVehicle'
import EditVehicle from './pages/client/EditVehicle'

// Admin Dashboard
import AdminLayout from './layouts/AdminLayout'
import AdminDashboard from './pages/admin/Dashboard'
import AdminClients from './pages/admin/Clients'
import AdminClientDetail from './pages/admin/ClientDetail'
import AdminVehicles from './pages/admin/Vehicles'
import AdminVehicleForm from './pages/admin/VehicleForm'
import AdminServiceRecords from './pages/admin/ServiceRecords'
import AdminAppointments from './pages/admin/Appointments'
import AdminMessages from './pages/admin/Messages'
import AdminInvitations from './pages/admin/Invitations'
import AdminWaitlist from './pages/admin/Waitlist'
import AdminWorkOrders from './pages/admin/WorkOrders'
import AdminConciergeLogs from './pages/admin/ConciergeLogs'
import AdminProviders from './pages/admin/Providers'

// Provider Portal
import ProviderLayout from './layouts/ProviderLayout'
import MyListing from './pages/provider/MyListing'

// Provider Claim
import ProviderClaim from './pages/client/ProviderClaim'
import ProviderVerifyResult from './pages/client/ProviderVerifyResult'

// Legal Pages
import PrivacyPolicy from './pages/legal/PrivacyPolicy'
import TermsOfService from './pages/legal/TermsOfService'
import SMSConsent from './pages/legal/SMSConsent'

// Public Pages
import VehicleShowcase from './pages/public/VehicleShowcase'
import UserProfile from './pages/public/UserProfile'
import Welcome from './pages/public/Welcome'
import Pricing from './pages/public/Pricing'
import PublicGarage from './pages/public/PublicGarage'

// Quick Add Vehicle
import QuickAddVehicle from './pages/client/QuickAddVehicle'

// Loading Spinner
const LoadingScreen = () => (
  <div className="min-h-screen bg-rdc-cream flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-rdc-primary border-t-transparent mx-auto mb-4"></div>
      <p className="text-rdc-taupe">Loading...</p>
    </div>
  </div>
)

// Route that allows both guests and authenticated users
const FlexibleRoute = ({ children }) => {
  const { loading } = useAuth()

  if (loading) {
    return <LoadingScreen />
  }

  return children
}

// Protected Route Component (for admin only)
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, profile, loading } = useAuth()

  if (loading) {
    return <LoadingScreen />
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (requireAdmin && profile?.role !== 'admin') {
    return <Navigate to="/garage" replace />
  }

  return children
}

// Public Route Component (redirects if already logged in)
const PublicRoute = ({ children }) => {
  const { user, profile, loading } = useAuth()

  if (loading) {
    return <LoadingScreen />
  }

  if (user) {
    // Redirect based on role
    if (profile?.role === 'admin') {
      return <Navigate to="/admin" replace />
    }
    return <Navigate to="/garage" replace />
  }

  return children
}

// Layout selector - uses ClientLayout for logged in, GuestLayout for guests
const AppLayout = () => {
  const { user, loading } = useAuth()

  if (loading) {
    return <LoadingScreen />
  }

  // Use appropriate layout based on auth status
  return user ? <ClientLayout /> : <GuestLayout />
}

function App() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><SignUp /></PublicRoute>} />
      <Route path="/signup/:token" element={<PublicRoute><SignUp /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Client Portal Routes - Accessible to both guests and logged in users */}
      <Route
        path="/"
        element={
          <FlexibleRoute>
            <AppLayout />
          </FlexibleRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="garage" element={<Garage />} />
        <Route path="garage/add" element={<QuickAddVehicle />} />
        <Route path="garage/add/full" element={<AddVehicle />} />
        <Route path="garage/:vehicleId" element={<VehicleDetail />} />
        <Route path="garage/:vehicleId/edit" element={<EditVehicle />} />
        <Route path="maintenance" element={<MaintenanceAlerts />} />
        <Route path="valuations" element={<Valuations />} />
        <Route path="discover" element={<Discover />} />
        <Route path="community" element={<Community />} />
        <Route path="clubs" element={<Clubs />} />
        <Route path="events" element={<Events />} />
        <Route path="specialists" element={<Specialists />} />
        <Route path="specialists/claim" element={<ProviderClaim />} />
        <Route path="specialists/claim/:providerId" element={<ProviderClaim />} />
        <Route path="specialists/verify" element={<ProviderVerifyResult />} />
        <Route path="service-history" element={<ServiceHistory />} />
        <Route path="service-history/add" element={<AddServiceRecord />} />
        <Route path="appointments" element={<Appointments />} />
        <Route path="concierge" element={<Concierge />} />
        <Route path="documents" element={<Documents />} />
        <Route path="settings" element={<Settings />} />
        <Route path="my-listing" element={<MyListing />} />
      </Route>

      {/* Admin Routes - Protected */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdmin>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="clients" element={<AdminClients />} />
        <Route path="clients/:clientId" element={<AdminClientDetail />} />
        <Route path="vehicles" element={<AdminVehicles />} />
        <Route path="vehicles/new" element={<AdminVehicleForm />} />
        <Route path="vehicles/:vehicleId/edit" element={<AdminVehicleForm />} />
        <Route path="service-records" element={<AdminServiceRecords />} />
        <Route path="appointments" element={<AdminAppointments />} />
        <Route path="messages" element={<AdminMessages />} />
        <Route path="invitations" element={<AdminInvitations />} />
        <Route path="waitlist" element={<AdminWaitlist />} />
        <Route path="work-orders" element={<AdminWorkOrders />} />
        <Route path="concierge-logs" element={<AdminConciergeLogs />} />
        <Route path="providers" element={<AdminProviders />} />
      </Route>

      {/* Public Pages */}
      <Route path="/welcome" element={<Welcome />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/v/:vehicleId" element={<VehicleShowcase />} />
      <Route path="/u/:userId" element={<UserProfile />} />
      <Route path="/g/:userId" element={<PublicGarage />} />

      {/* Legal Pages - Public */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/sms-consent" element={<SMSConsent />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
